import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { useScheduleActivityAccess } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { styles, HintScroll } from '@frontend/design-system';
import { DATETIME_FORMATS, PRIMARY_EVENT_COLOR } from '../../../constants';
import { useGetEntityArchiveStatus } from '../../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { stripDateOrdinals } from '../../../utils';
import { OverlappingEventIndicator } from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { AppointmentStatusV2 } from '../../schedule-calendar-components/appointment-event-card/appointment-status-v2';
import { EventCardAppointmentStatusChipV3 } from '../../v3/EventCardAppointmentStatusChip';
import { ColorDot } from '../color-dot';
import { ActivityHistory } from './activity-history';
import { AppointmentColorPicker } from './appointment-color-picker';
import { useApptPanelContext } from './appt-panel-context';
import { DetailItem } from './item-detail';
import { PanelTabs } from './panel-tabs';
import { useGetLocationAddress } from './use-get-location-address';

export const PanelDetails = () => {
  const { t } = useTranslation('schedule');
  const tokens = useTranslationTokens();
  const { isScheduleActivityEnabled } = useScheduleActivityAccess();
  const { getScopeName, locationData, isMultiLocation } = useSchedulingLocationInfo();
  const { apptDetails, isV3Enabled, locationId, shouldShowOperatory } = useApptPanelContext();
  const details = apptDetails?.appointmentDetails;

  const { isAppointmentTypeArchived, isProviderArchived, providerCalendarId } = useGetEntityArchiveStatus({
    appointmentTypeId: details?.appointmentTypeId || '',
    providerId: details?.providerIds?.[0] || '',
    locationId: locationId,
  });
  const locationAddressText = useGetLocationAddress(locationData[locationId]?.slug);

  const apptId = apptDetails?.id ?? '';
  const apptTypeName = details?.type || '';
  const apptTypeColor = details?.displayOptions?.primaryColor || PRIMARY_EVENT_COLOR;
  const providerName = details?.practitioner ?? '';
  const operatoryName = details?.operatory || '';
  const status = details?.status;
  const startDate = details?.date ? formatDate(stripDateOrdinals(details?.date), DATETIME_FORMATS.ISO_DATE) : '';
  const referenceId = details?.referenceId || '';
  const dateString = formatDate(startDate, 'ddd, MMMM DD, YYYY');
  const timeString = details?.duration || '-';
  const locationName = getScopeName(locationId);

  return (
    <HintScroll css={contentContainer}>
      <div css={[styles.flexCenter, { gap: theme.spacing(1), alignSelf: 'flex-end' }]}>
        {status &&
          (isV3Enabled ? (
            <EventCardAppointmentStatusChipV3
              locationId={locationId}
              appointmentId={apptId}
              appointmentStatus={status}
              appointmentStartDate={startDate}
              appointmentReferenceId={referenceId}
            />
          ) : (
            <AppointmentStatusV2 locationId={locationId} appointmentId={apptId} status={status} />
          ))}
        <AppointmentColorPicker providerCalendarId={providerCalendarId} />
      </div>
      {isMultiLocation && (
        <DetailItem
          label={t('Location')}
          title={locationName}
          content={locationAddressText}
          leftElement={<Icon name='location-small' color='light' />}
          copyableText={[locationName, locationAddressText].filter(Boolean).join(', ')}
        />
      )}
      <DetailItem
        label={t('Date & Time')}
        title={dateString}
        content={timeString}
        leftElement={<Icon name='calendar-small' color='light' />}
        rightElement={
          isV3Enabled && <OverlappingEventIndicator variant='full' visible={details?.isOverlapping} borderless />
        }
        copyableText={[dateString, timeString].filter(Boolean).join(', ')}
      />
      <DetailItem
        label={t('Appointment Type')}
        isArchived={isAppointmentTypeArchived}
        title={apptTypeName}
        leftElement={<ColorDot color={apptTypeColor} />}
      />
      {shouldShowOperatory && (
        <DetailItem
          label={tokens.operatory}
          title={operatoryName}
          leftElement={<Icon name='department-small' color='light' />}
        />
      )}
      <DetailItem
        label={t('Provider')}
        title={providerName}
        isArchived={isProviderArchived}
        leftElement={<Icon name='user-small' color='light' />}
      />
      {isScheduleActivityEnabled && (
        <DetailItem
          label={t('History')}
          isArchived={isProviderArchived}
          content={<ActivityHistory locationId={locationId} referenceId={referenceId} />}
        />
      )}
      <PanelTabs />
    </HintScroll>
  );
};

const contentContainer = css({
  flex: 1,
  overflowY: 'auto',
  '& >:last-child': {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    gap: theme.spacing(1),
  },
});
